// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    resources = "resources",
    isRequired = "isRequired",
}

interface ResourceModel {
    fields: {
        resource: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.resources]: ResourceModel[];
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    resource: state.components.contactUsForm.fieldValues["resource"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AvailableResourcesStyles {
    resourcesSelect?: SelectPresentationProps;
}

export const availableResourcesStyles: AvailableResourcesStyles = {};

export const styles = availableResourcesStyles;

const AvailableResources: React.FC<Props> = ({ fields, resource, setFieldValue }) => {
    const { label, hintText, resources, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [resourceErrorMessage, setResourceErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.resource = validateResource;
        if (typeof resource !== "undefined") {
            validateResource();
        }
    }, [resource]);

    React.useEffect(() => {
        contactFormContext.validators.resource = validateResource;
    }, [isRequired]);

    const validateResource = () => {
        const errorMessage = isRequired && !resource ? (siteMessage("Field_Required", label) as string) : "";
        setResourceErrorMessage(errorMessage);
        return !errorMessage;
    };

    const resourceChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setFieldValue({ key: "resource", value: event.target.value });
    };

    return (
        <Select
            {...styles.resourcesSelect}
            label={label}
            required={isRequired}
            value={resource || ""}
            error={resourceErrorMessage}
            onChange={resourceChangeHandler}
        >
            <option value="">{hintText}</option>
            {resources.map(resource => (
                <option key={resource.fields.resource} value={resource.fields.resource}>
                    {resource.fields.resource}
                </option>
            ))}
        </Select>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AvailableResources),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Resources",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Select Option",
                isRequired: true,
            },
            {
                name: fields.resources,
                displayName: "Options",
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.resource;
                },
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "resource",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
